import axios from 'axios';
import type CamlinSocketHandler from './CamlinSocketHandler';
import { initializeVoiceCall, makeCall } from './voiceCallFeature';
import type { AxiosResponse } from 'axios';
import { RefObject } from 'react';

// Define interface for props
interface VoiceHandlerProps {
  avatarRef: React.RefObject<any>;
  handoffTokenCamlin: string;
  botDisplayStatusSession: string;
  showToast: (msg: string, type?: 'info' | 'success' | 'warning' | 'error') => void;
  sendMsgToBot: (msg: string, direction: string) => void;
  handleVisemeData: (eventBody: any, ht: string) => Promise<void>;
  hangupNow: () => void;
  setUserTalking: (value: boolean) => void;
  voiceUrl: string;
  isVoiceCallAnimationInProgress: boolean;
  makeSpeech: (text: string) => Promise<any>;
  ttsUrl: string;
  setAudioSource: (source: string | null) => void;
  initializeVoiceCall: () => any;
  makeCall: (ua: any, target: string) => any;
  setAnimateCallSpeak: (value: { blendData: any; handoffToken?: string } | boolean) => void;
  currentSession?: any;
  socketHandlerRef?: RefObject<CamlinSocketHandler>;
  refreshUIButtonsOnCallEnd?: () => void;
  setIsInVoiceCall?: (value: boolean) => void;
  setIsMobileCall?: (value: boolean) => void;
  setIsWebrtcCall?: (value: boolean) => void;
  lastHangupTime?: number;
  setLastHangupTime?: (time: number) => void;
  hangupDebounceTime?: number;
  setDisplayText?: (text: string) => void;
  onTranscriptionUpdate?: (text: string) => void;
  audioPlayerRef?: React.RefObject<any>;
  onEndCallKeepSession?: () => void;
  // Add more as needed
}

export const handleEvent = (eventName: string, data: any, props?: Partial<VoiceHandlerProps>) => {
  const { avatarRef, handoffTokenCamlin, botDisplayStatusSession, showToast, sendMsgToBot, handleVisemeData, hangupNow, setUserTalking, setDisplayText, onTranscriptionUpdate, audioPlayerRef, setAnimateCallSpeak, setAudioSource, setIsMobileCall, setIsWebrtcCall, setIsInVoiceCall, onEndCallKeepSession } = props ?? {};
  try {
    console.log('Handling event:', eventName);
    if ("Connection Established" === eventName) {
      console.log('Connection Established');
      return;
    }
    switch (eventName) {
      case 'camlin.runtime.voice.tts.viseme':
        // Avatar is speaking - IMMEDIATELY stop and reset skeletal animations
        // BUT: Don't stop thinking animation - it can run alongside speaking (different body parts)
        // NOTE: Do NOT stop listening here - listening should only stop when:
        // 1. User stops talking (talking.stopped event)
        // 2. Thinking animation starts (response being processed)
        // 3. Response arrives (even if "I don't understand")
        if (avatarRef?.current) {
          console.log('🎬 Viseme starting - preserving current head pose for speech');

          // DO NOT stop listening here - viseme means avatar is speaking, not that listening should stop
          // Listening animation should continue until user finishes talking or thinking starts
          // DO NOT stop thinking - it's head/eye movement that doesn't conflict with lip-sync

          // SECOND: Cancel any listening/thinking loops without recentering the head.
          if (typeof avatarRef.current.forceResetSkeletalAnimations === 'function') {
            avatarRef.current.forceResetSkeletalAnimations();
          }

          const mixer = avatarRef.current.getMixer?.();
          const scene = mixer?._root;
          // THIRD: Set neutral mouth baseline for speech
          // Reset mouth to nearly-closed so viseme data can properly control it
          // Mood eyes/brows stay active, but mouth needs neutral for consonants (m, b, p, t, d)
          const morphDict = avatarRef.current.getMorphTargetDictionary?.();
          if (mixer && morphDict?.body && scene) {
            scene.traverse((node: any) => {
              if (node.morphTargetInfluences && node.name.includes('Body')) {
                // Set mouth to nearly-closed baseline
                const dict = node.morphTargetDictionary;
                if (dict) {
                  // Reset mouth position blendshapes
                  if ('mouthClose' in dict) node.morphTargetInfluences[dict.mouthClose] = 0.15;  // Nearly closed
                  if ('mouthSmile_L' in dict) node.morphTargetInfluences[dict.mouthSmile_L] = 0;
                  if ('mouthSmile_R' in dict) node.morphTargetInfluences[dict.mouthSmile_R] = 0;
                  if ('mouthFrown_L' in dict) node.morphTargetInfluences[dict.mouthFrown_L] = 0;
                  if ('mouthFrown_R' in dict) node.morphTargetInfluences[dict.mouthFrown_R] = 0;
                  if ('mouthPucker' in dict) node.morphTargetInfluences[dict.mouthPucker] = 0;
                  if ('mouthFunnel' in dict) node.morphTargetInfluences[dict.mouthFunnel] = 0;
                  if ('jawOpen' in dict) node.morphTargetInfluences[dict.jawOpen] = 0.05;  // Slightly relaxed
                  console.log('🗣️ Mouth baseline set for speech (nearly closed, ready for consonants)');
                }
              }
            });
          }

          // Don't call stopAllAnimations - moods (eyes/brows) continue during speaking!
        }
        // Show what avatar is saying
        if (data.utterance) {
          const utterance = data.utterance.trim();
          const wordCount = utterance.split(/\s+/).filter((w: string) => w.length > 0).length;
          setDisplayText?.(`🗣️ Avatar (${wordCount} words):\n"${utterance}"`);
        }
        handleVisemeData?.(data, handoffTokenCamlin ?? '');
        break;
      case 'camlin.runtime.voice.tts.viseme.webrtc':
        // Avatar is speaking - IMMEDIATELY stop and reset skeletal animations
        // BUT: Don't stop thinking animation - it can run alongside speaking (different body parts)
        // NOTE: Do NOT stop listening here - listening should only stop when user stops talking or thinking starts
        if (avatarRef?.current) {
          console.log('🎬 Viseme (WebRTC) starting - preserving current head pose for speech');

          // DO NOT stop listening here - viseme means avatar is speaking, not that listening should stop
          // Listening animation should continue until user finishes talking or thinking starts
          // DO NOT stop thinking - it's head/eye movement that doesn't conflict with lip-sync

          // SECOND: Cancel any listening/thinking loops without recentering the head.
          if (typeof avatarRef.current.forceResetSkeletalAnimations === 'function') {
            avatarRef.current.forceResetSkeletalAnimations();
          }

          const mixer = avatarRef.current.getMixer?.();
          const scene = mixer?._root;
          // THIRD: Set neutral mouth baseline for speech
          const morphDict = avatarRef.current.getMorphTargetDictionary?.();
          if (mixer && morphDict?.body && scene) {
            scene.traverse((node: any) => {
              if (node.morphTargetInfluences && node.name.includes('Body')) {
                const dict = node.morphTargetDictionary;
                if (dict) {
                  // Reset mouth to nearly-closed for proper consonant pronunciation
                  if ('mouthClose' in dict) node.morphTargetInfluences[dict.mouthClose] = 0.15;
                  if ('mouthSmile_L' in dict) node.morphTargetInfluences[dict.mouthSmile_L] = 0;
                  if ('mouthSmile_R' in dict) node.morphTargetInfluences[dict.mouthSmile_R] = 0;
                  if ('mouthFrown_L' in dict) node.morphTargetInfluences[dict.mouthFrown_L] = 0;
                  if ('mouthFrown_R' in dict) node.morphTargetInfluences[dict.mouthFrown_R] = 0;
                  if ('mouthPucker' in dict) node.morphTargetInfluences[dict.mouthPucker] = 0;
                  if ('mouthFunnel' in dict) node.morphTargetInfluences[dict.mouthFunnel] = 0;
                  if ('jawOpen' in dict) node.morphTargetInfluences[dict.jawOpen] = 0.05;
                  console.log('🗣️ Mouth baseline set for speech (WebRTC)');
                }
              }
            });
          }

          // Don't call stopAllAnimations - moods (eyes/brows) continue during speaking!
        }
        // Show what avatar is saying
        if (data.utterance) {
          const utterance = data.utterance.trim();
          const wordCount = utterance.split(/\s+/).filter((w: string) => w.length > 0).length;
          setDisplayText?.(`🗣️ Avatar (${wordCount} words):\n"${utterance}"`);
        }
        handleVisemeData?.(data, data.handoffToken ?? '');
        break;
      case 'camlin.runtime.voice.hangup':
        // Check if we should continue the session (from WebSocket response data)
        // Backend sends: response.setData({ continueSession: true, timestamp: ... })
        // So data structure is: { type: '...', data: { continueSession: true, ... } }
        // Check both data.data.continueSession and data.continueSession as fallback
        const continueSessionValue = data?.data?.continueSession ?? data?.continueSession;
        const continueSession = continueSessionValue !== false; // Default to true if not set or undefined
        console.log('📞 Hangup event received:', {
          continueSession,
          continueSessionValue,
          dataStructure: {
            hasData: !!data,
            hasDataData: !!data?.data,
            dataDataContinueSession: data?.data?.continueSession,
            dataContinueSession: data?.continueSession,
            fullData: data
          }
        });

        if (continueSession) {
          // Continue session - reset flags but keep session active
          console.log('✅ Continuing session after hangup');
          showToast?.('Call ended - Use speech button to continue', 'success');
          setDisplayText?.('📞 Call ended - Use Speech button to continue');

          // Reset call flags immediately so message handler works
          setIsMobileCall?.(false);
          setIsWebrtcCall?.(false);
          setIsInVoiceCall?.(false);

          if (avatarRef?.current) {
            avatarRef.current.stopAllAnimations();
            if (typeof avatarRef.current.playIdleAnimation === 'function') {
              avatarRef.current.playIdleAnimation();
            }
          }

          // Call the callback to trigger speech button highlight
          if (props?.onEndCallKeepSession) {
            props.onEndCallKeepSession();
          }
        } else {
          // End session - full hangup
          console.log('🛑 Ending session (full hangup)');
          showToast?.('Call has ended', 'success');
          if (avatarRef?.current) {
            avatarRef.current.stopAllAnimations();
            if (typeof avatarRef.current.playIdleAnimation === 'function') {
              avatarRef.current.playIdleAnimation();
            }
          }
          setDisplayText?.('📞 Call ended');
          hangupNow?.();
        }
        break;
      case 'camlin.runtime.voice.talking.finished':
        setUserTalking?.(false);
        setDisplayText?.('🤔 Processing your request...');
        if (avatarRef?.current) {
          // DON'T call stopAllAnimations here - preserves any ongoing animations
          // Just transition states - skeletal animations will handle the rest
          avatarRef.current.setIsListening(false);

          // Delay thinking animation slightly to allow tts.stopped to process first
          // This prevents competition between thinking and stop events
          setTimeout(() => {
            if (avatarRef?.current) {
              avatarRef.current.setIsThinking(true);
            }
          }, 50); // 50ms delay allows tts.stopped to arrive first
          setTimeout(() => {
            console.log('Clearing thinking state');
            try {
              if (avatarRef.current) {
                avatarRef.current.setIsThinking(false);
                // Only call playIdleAnimation if no voice animation is running
                if (typeof avatarRef.current.playIdleAnimation === 'function') {
                  avatarRef.current.playIdleAnimation();
                }
              }
            } catch (error) {
              console.error('Error during thinking state cleanup:', error);
            }
          }, 2000);
        }
        break;
      case 'camlin.runtime.voice.talking.started':
        // USER started speaking - INTERRUPT avatar if it's talking
        // BUT: Only if speaking animation is not protected (audio actually stopped)
        console.log('🎤 User started speaking - checking if can interrupt avatar');
        setDisplayText?.('🎤 Listening...');

        // Check if we should actually interrupt (audio might have already stopped)
        // The avatar:stop handler will check protection, so we can safely dispatch
        // Stop any ongoing avatar speech (audio + animation)
        if (audioPlayerRef?.current?.audioEl?.current) {
          console.log('Stopping avatar audio playback');
          audioPlayerRef.current.audioEl.current.pause();
          audioPlayerRef.current.audioEl.current.currentTime = 0;
        }

        // Dispatch stop event - the handler will check protection and audio state
        // This respects the priority system and protection flags
        console.log('🛑 Requesting avatar animation stop (talking.started)');
        window.dispatchEvent(new CustomEvent('avatar:stop', {
          detail: {
            stopReason: 'talking_started',
            timestamp: Date.now(),
            priority: 'high'
          }
        }));

        // Stop the animation state (will be ignored if protected)
        setAnimateCallSpeak?.(false);

        // Stop TTS animation (will be ignored if protected)
        if (setAnimateCallSpeak) {
          console.log('Requesting avatar lip-sync animation stop');
          setAnimateCallSpeak(false);
        }

        // Clear audio source
        if (setAudioSource) {
          setAudioSource(null);
        }

        // Set listening state
        if (avatarRef?.current) {
          console.log('Setting listening state (user is now speaking)');
          // Don't stop thinking - it can continue during listening
          avatarRef.current.setIsListening(true);
        }

        setUserTalking?.(true);
        break;
      case 'camlin.runtime.voice.stt.recognizing':
        // Real-time transcription update (intermediate/accumulating results from Azure STT)
        // Note: data.utterance contains the FULL accumulated text so far, not just delta
        // Sentiment analysis runs on EACH update to catch mood changes mid-sentence
        if (data.utterance) {
          const utterance = data.utterance.trim();
          console.log(`📝 Transcription accumulating (${utterance.length} chars):`, utterance);

          // Format display with word count for feedback
          const wordCount = utterance.split(/\s+/).filter((w: string) => w.length > 0).length;
          setDisplayText?.(`🎤 You're saying (${wordCount} words):\n"${utterance}..."`);

          // Trigger sentiment analysis on partial transcription for real-time mood changes
          // This allows: "I have a problem" → concerned, then "...but I'm happy now" → positive
          onTranscriptionUpdate?.(utterance);
        }
        break;
      case 'camlin.runtime.voice.stt.result':
        // Final transcription result (complete utterance)
        if (data.utterance) {
          const utterance = data.utterance.trim();
          const wordCount = utterance.split(/\s+/).filter((w: string) => w.length > 0).length;
          console.log(`✅ Final transcription (${wordCount} words):`, utterance);
          setDisplayText?.(`✓ You said (${wordCount} words):\n"${utterance}"`);
          // Trigger final sentiment analysis
          onTranscriptionUpdate?.(utterance);
        }
        if ('open' !== botDisplayStatusSession) {
          showToast?.(data.utterance, 'info');
        }
        sendMsgToBot?.(data.utterance, 'out');
        break;
      case 'camlin.runtime.voice.stt.issue':
        showToast?.("I didn't quite understand", 'warning');
        setDisplayText?.('⚠️ Speech not recognized - please try again');
        setUserTalking?.(true);
        break;
      default:
        console.log('Received event:', eventName, data);
    }
  } catch (error) {
    console.error('Event handling error:', error);
  }
};

// Similar for handleVisemeData, add props
interface VisemeHandlerProps extends VoiceHandlerProps {
  isMobileCall: boolean;
  isWebrtcCall: boolean;
  isInVoiceCall?: boolean;  // Runtime voice calls (not mobile/webrtc)
  playIdleAnimation: () => void;
}

// Helper to wait for avatar to be ready with timeout
const waitForAvatarReady = async (avatarRef: React.RefObject<any>, maxWaitMs: number = 10000): Promise<boolean> => {
  const startTime = Date.now();
  const checkInterval = 100; // Check every 100ms

  while (Date.now() - startTime < maxWaitMs) {
    if (avatarRef?.current?.isReady?.()) {
      return true;
    }
    await new Promise(resolve => setTimeout(resolve, checkInterval));
  }

  console.warn('Avatar did not become ready within timeout');
  return false;
};

export const handleVisemeData = async (eventBody: any, ht: string, props: VisemeHandlerProps) => {
  const { avatarRef, setAnimateCallSpeak, isMobileCall, isWebrtcCall, isInVoiceCall, voiceUrl, isVoiceCallAnimationInProgress, sendMsgToBot, makeSpeech, ttsUrl, setAudioSource } = props;
  try {
    console.log('handleVisemeData:', eventBody.utterance ? eventBody.utterance.substring(0, 20) + '...' : 'No utterance');

    // Wait for avatar to be ready before processing visemes
    const isReady = avatarRef?.current?.isReady?.() ?? false;
    if (!isReady) {
      console.log('⏳ Avatar not ready yet, waiting for model to load...');

      // Dispatch loading event to show UI message
      window.dispatchEvent(new CustomEvent('avatar:loading', {
        detail: { message: 'Setting up your virtual assistant. Give me a few seconds please...' }
      }));

      const becameReady = await waitForAvatarReady(avatarRef);
      if (!becameReady) {
        console.error('Avatar failed to become ready, cannot process visemes');
        return;
      }

      console.log('✅ Avatar is now ready, proceeding with viseme data');

      // Dispatch ready event to clear loading message
      window.dispatchEvent(new CustomEvent('avatar:loading:complete', {
        detail: { message: 'Ready!' }
      }));
    }

    console.log('Avatar state:', {
      hasAvatarRef: !!avatarRef?.current,
      isAvatarReady: avatarRef?.current?.isReady() ?? false,
      isMobileCall,
      isWebrtcCall,
      isInVoiceCall
    });
    // If we're receiving viseme data from camlin.runtime.voice.tts.viseme, it's ALWAYS a voice call
    // Check state flags, but also treat it as voice call if we have viseme data from runtime
    // This handles timing issues where state might not be updated yet
    const isVoiceCall = isMobileCall || isWebrtcCall || isInVoiceCall || !!eventBody.handoffToken;
    if (isVoiceCall && avatarRef?.current) {
      const mixer = avatarRef.current.getMixer?.();
      const morphDicts = avatarRef.current.getMorphTargetDictionary?.();
      console.log('Fast path check:', {
        hasMixer: !!mixer,
        hasMorphDicts: !!morphDicts,
        hasMorphBody: !!morphDicts?.body,
        hasMorphTeeth: !!morphDicts?.teeth
      });
      if (mixer && morphDicts && morphDicts.body && morphDicts.teeth) {
        console.log('Fast path check passed, processing viseme data');
        // Note: Don't send to bot here - it's sent later at line ~299 to avoid duplicates
        console.log('Voice call detected - fetching viseme data from Camlin Voice');
        const handoffToken = eventBody.handoffToken || ht;
        if (!handoffToken) {
          console.warn('No handoff token available');
          return;
        }
        const response: AxiosResponse<{ blendData: any }> = await axios.get(`${voiceUrl}/viseme/${handoffToken}`);

        // Don't manipulate avatar state before animation - let the animation handler do it
        // Changing thinking/listening states triggers other animations that interfere
        if (response.data && response.data.blendData) {
          console.log('Successfully fetched viseme data from Camlin Voice');
          const blendData = ensureCorrectBlendDataFormat(response.data.blendData);
          // Pass handoffToken to enable duplicate detection
          setAnimateCallSpeak({ blendData, handoffToken });
          console.log('Voice call animation started via animateCallSpeak', { handoffToken });
        } else {
          console.warn('No blend data received from Camlin Voice server');
          const minimalBlendData = {
            body: [{ frame: 0, blendshapes: { jawOpen: 0.1 } }],
            lowerTeeth: [{ frame: 0, blendshapes: {} }]
          };
          setAnimateCallSpeak({ blendData: minimalBlendData, handoffToken });
        }
      }
    }
    if (eventBody.utterance) {
      sendMsgToBot?.(eventBody.utterance, 'in');
    }
    // Only use makeSpeech if it's NOT a voice call (no viseme data should come through here for voice calls)
    // If viseme data is present, it's always a voice call, so skip makeSpeech
    if (!isVoiceCall && !eventBody.blendData) {
      console.log('Non-voice call detected - using makeSpeech for TTS');
      try {
        const utteranceText = eventBody.utterance || "Hello";
        const response = await makeSpeech(utteranceText);
        console.log('makeSpeech response received');
        const { blendData, audioData, filename } = response.data;
        if (!blendData) {
          console.error('Invalid blend data received from makeSpeech');
          return;
        }

        // Check if we have in-memory audio data (new, faster method)
        if (audioData) {
          // Use data URL for instant playback (no file I/O needed)
          const audioDataUrl = `data:audio/mpeg;base64,${audioData}`;
          console.log('Setting audio source to data URL (in-memory audio)');
          setAudioSource(audioDataUrl);
        } else if (filename) {
          // Fallback to file-based method (for backwards compatibility)
          // Filename from TTS service may have leading slash (e.g., "/speech-xxx.mp3")
          // Audio endpoint expects: /audio/speech-xxx.mp3 (no leading slash in filename part)
          const cleanFilename = filename.startsWith('/') ? filename.substring(1) : filename;
          // If ttsUrl is empty, use relative path (will go through nginx proxy)
          // Otherwise use full URL
          const audioFilename = ttsUrl ? `${ttsUrl}/audio/${cleanFilename}` : `/audio/${cleanFilename}`;
          console.log('Setting audio source for TTS:', audioFilename);
          setAudioSource(audioFilename);
        } else {
          console.error('TTS response missing both audioData and filename');
          return;
        }
      } catch (speechError) {
        console.error('Error using makeSpeech:', speechError);
        if (avatarRef?.current && avatarRef.current.playIdleAnimation) {
          avatarRef.current.playIdleAnimation();
        }
      }
    }
  } catch (error) {
    console.error('Error in handleVisemeData:', error);
  }
};

function ensureCorrectBlendDataFormat(blendData: any): any[] {
  if (!blendData) return [];
  if (Array.isArray(blendData) && blendData.length > 0 && blendData[0].blendshapes && blendData[0].time !== undefined) {
    return blendData;
  }
  if (Array.isArray(blendData)) {
    return blendData.map((item, index) => {
      if (item.blendshapes && item.time !== undefined) {
        return item;
      }
      if (typeof item === 'object' && !Array.isArray(item)) {
        return {
          blendshapes: item,
          time: index / 60
        };
      }
      return {
        blendshapes: {},
        time: index / 60
      };
    });
  }
  if (typeof blendData === 'object' && !Array.isArray(blendData)) {
    return [{
      blendshapes: blendData,
      time: 0
    }];
  }
  return [];
}

export function initiateCall(target: string, isVoiceInitialized: boolean, ua: any, setUa: (ua: any) => void, setIsVoiceInitialized: (value: boolean) => void, setCurrentSession: (session: any) => void, setIsInVoiceCall: (value: boolean) => void, showToast: (msg: string, type: string) => void) {
  if (!isVoiceInitialized || !ua) {
    console.log('Voice features not initialized yet, initializing now...');
    try {
      const userAgent = initializeVoiceCall();
      if (userAgent && userAgent.ua) {
        setUa(userAgent.ua);
        setIsVoiceInitialized(true);
        const session = makeCall(userAgent.ua, target);
        setCurrentSession(session);
      } else {
        showToast('Failed to initialize voice call', 'error');
      }
    } catch (error) {
      console.error('Error initializing voice call:', error);
      showToast('Failed to initialize voice call', 'error');
    }
  } else {
    console.log('Making call with existing WebRTC connection');
    const session = makeCall(ua, target);
    setCurrentSession(session);
  }
  setIsInVoiceCall(true);
}

export function hangupNow(props: VoiceHandlerProps) {
  const { currentSession, socketHandlerRef, avatarRef, refreshUIButtonsOnCallEnd, showToast, lastHangupTime, setLastHangupTime, hangupDebounceTime, setIsMobileCall, setIsWebrtcCall, setIsInVoiceCall } = props;
  const now = Date.now();
  if (lastHangupTime !== undefined && hangupDebounceTime !== undefined && now - lastHangupTime < hangupDebounceTime) {
    console.log('Hangup function debounced, ignoring');
    return;
  }
  setLastHangupTime?.(now);
  console.log('Executing hangupNow...');
  if (socketHandlerRef?.current) {
    console.log('Sending hangup event to Camlin runtime');
    socketHandlerRef.current.sendHangupEvent();
  }
  if (currentSession) {
    console.log('Terminating SIP session');
    currentSession.terminate();
  }
  if (avatarRef?.current) {
    console.log('Resetting avatar animations');
    avatarRef.current.stopAllAnimations();
    if (typeof avatarRef.current.playIdleAnimation === 'function') {
      avatarRef.current.playIdleAnimation();
    }
  }
  setIsMobileCall?.(false);
  setIsWebrtcCall?.(false);
  refreshUIButtonsOnCallEnd?.();
  showToast?.('Call ended', 'success');
  setIsInVoiceCall?.(false);
}

export function confirmAndHangup(lastHangupTime: number, hangupDebounceTime: number, hangupNow: () => void) {
  const now = Date.now();
  if (now - lastHangupTime < hangupDebounceTime) {
    console.log('Confirmation dialog debounced, ignoring');
    return;
  }

  if (window.confirm('Are you sure you want to end the call?')) {
    hangupNow();
  }
}
